import { HEN_BREEDS } from '../data/breeds';
import { TICK_MS, WIN_BALANCE, WIN_HENS } from '../data/balance';
import { recordEggsCollected } from './actions';
import { checkAchievements, checkChallenges } from './achievements';
import { tickBroody } from './broody';
import { buffProduct, pruneBuffs } from './buffs';
import { bankAccelerator, updateCombo } from './combo';
import { tickCritters } from './critters';
import { crow } from './actions';
import { earn, farmTotals, gatherRate, henStats, nestCapacity } from './economy';
import { tickEvents } from './events';
import { globalAdd } from './modifiers';
import { checkBreedUnlocks } from './requirements';
import { countHens, countRoosters, emit, logActivity } from './state';
import type { Flock, GameState, NestBox, SimCtx } from './types';

/**
 * One fixed simulation step (default 100 ms of sim time). Pure with respect
 * to its inputs: time comes from `s.time += dtMs` and `ctx.wall`, randomness
 * from the seeded RNG in state.
 */
export function tick(s: GameState, ctx: SimCtx, dtMs: number = TICK_MS): void {
  s.time += dtMs;
  s.wallAt = ctx.wall;
  s.stats.activePlaySec += dtMs / 1000;
  s.stats.lifetimeActivePlaySec += dtMs / 1000;

  pruneBuffs(s.buffs.lay, s.time);
  pruneBuffs(s.buffs.click, s.time);
  pruneBuffs(s.buffs.auto, s.time);
  pruneBuffs(s.buffs.gather, s.time);
  for (const f of s.flocks) pruneBuffs(f.crowBuffs, s.time);

  layEggs(s, ctx, dtMs);
  autoGather(s, ctx, dtMs);

  updateCombo(s, s.time);
  bankAccelerator(s, dtMs);

  tickEvents(s, ctx, dtMs);
  tickCritters(s, ctx);
  tickBroody(s, ctx);

  if (globalAdd(s, ctx, 'autoCrow') > 0) {
    for (const f of s.flocks) if (f.rooster && s.time >= f.rooster.crowReadyAt) crow(s, ctx, f.id);
  }

  // Slower bookkeeping once per sim-second.
  if (Math.floor(s.time / 1000) !== Math.floor((s.time - dtMs) / 1000)) {
    checkBreedUnlocks(s);
    checkAchievements(s, ctx);
    checkWin(s, ctx);
    checkChallenges(s);
    const income = farmTotals(s, ctx, true).passiveIncome;
    if (income > s.stats.peakIncome) s.stats.peakIncome = income;
  }
}

function layEggs(s: GameState, ctx: SimCtx, dtMs: number): void {
  for (const f of s.flocks) {
    for (const b of f.boxes) {
      const hen = b.hen;
      if (!hen || hen.broodySince !== null) continue;
      const { layRate } = henStats(s, ctx, f, hen);
      hen.progress += (layRate * dtMs) / 1000;
      while (hen.progress >= 1) {
        hen.progress -= 1;
        s.eggsLaid++;
        if (b.eggs < nestCapacity(b)) {
          b.eggs++;
          b.oldestEggAt ??= s.time;
        } else {
          s.stats.eggsLost++;
        }
      }
    }
  }
}

const gatherAcc = new WeakMap<GameState, number>();

/** Gatherers empty the fullest nests first (fewest lost eggs). */
function autoGather(s: GameState, ctx: SimCtx, dtMs: number): void {
  const rate = gatherRate(s, ctx);
  if (rate <= 0) return;
  let acc = (gatherAcc.get(s) ?? 0) + (rate * dtMs) / 1000;
  let collected = 0;
  let value = 0;
  while (acc >= 1) {
    let best: { f: Flock; b: NestBox } | null = null;
    for (const f of s.flocks) {
      for (const b of f.boxes) {
        if (!b.hen || b.eggs <= 0) continue;
        if (!best || b.eggs > best.b.eggs || (b.eggs === best.b.eggs && (b.oldestEggAt ?? 0) < (best.b.oldestEggAt ?? 0))) {
          best = { f, b };
        }
      }
    }
    if (!best) {
      acc = Math.min(acc, 1);
      break;
    }
    const st = henStats(s, ctx, best.f, best.b.hen!);
    value += st.eggValue * st.autoMult;
    best.b.eggs--;
    if (best.b.eggs <= 0) best.b.oldestEggAt = null;
    acc -= 1;
    collected++;
  }
  gatherAcc.set(s, acc);
  if (collected) {
    earn(s, value);
    recordEggsCollected(s, collected);
  }
}

function checkWin(s: GameState, _ctx: SimCtx): void {
  if (s.won) return;
  if (s.money >= WIN_BALANCE && countHens(s) >= WIN_HENS && countRoosters(s) >= 1) {
    s.won = true;
    s.wonWall = s.wallAt;
    emit(s, { kind: 'win' });
    logActivity(s, 'milestone', 'Stage 1 cleared', '$1,000,000 with 15 hens and a rooster');
  }
}

/** True when any transient buff is active (for UI badges). */
export function buffsActive(s: GameState): boolean {
  return buffProduct(s.buffs.lay, s.time) > 1 || buffProduct(s.buffs.click, s.time) > 1 ||
    buffProduct(s.buffs.auto, s.time) > 1 || buffProduct(s.buffs.gather, s.time) > 1;
}

export function isBroodyProne(breed: keyof typeof HEN_BREEDS): boolean {
  return HEN_BREEDS[breed].broodyProne;
}
